import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar,
} from 'recharts';
import { farmApi, indicesApi } from '../lib/api';
import { format, subDays } from 'date-fns';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export default function Analytics() {
  const [dateRange, setDateRange] = useState('30');

  const { data: farms = [], isLoading: farmsLoading } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  // Fetch NDVI trend data for all farms
  const { data: ndviTrendData } = useQuery({
    queryKey: ['analytics-ndvi-trend', farms.map(f => f.id).join(','), dateRange],
    queryFn: async () => {
      if (farms.length === 0) return [];

      const endDate = new Date().toISOString().split('T')[0];
      const startDate = subDays(new Date(), parseInt(dateRange)).toISOString().split('T')[0];

      // Fetch NDVI data for each farm
      const allData: Map<string, number[]> = new Map();

      for (const farm of farms) {
        try {
          const result = await indicesApi.getTimeSeries(farm.id, 'ndvi', startDate, endDate);
          if (result.data && result.data.length > 0) {
            for (const item of result.data) {
              const dateKey = item.date;
              if (!allData.has(dateKey)) {
                allData.set(dateKey, []);
              }
              allData.get(dateKey)!.push(item.meanValue);
            }
          }
        } catch {
          // Skip farms with no data
        }
      }

      // Convert to array and calculate averages
      const trendData = Array.from(allData.entries())
        .map(([date, values]) => ({
          date: format(new Date(date), 'MMM d'),
          rawDate: date,
          value: values.reduce((sum, v) => sum + v, 0) / values.length,
        }))
        .sort((a, b) => new Date(a.rawDate).getTime() - new Date(b.rawDate).getTime());

      return trendData;
    },
    enabled: farms.length > 0,
  });

  // Fetch farm stats for comparison
  const { data: farmStatsData } = useQuery({
    queryKey: ['analytics-farm-stats', farms.map(f => f.id).join(',')],
    queryFn: async () => {
      if (farms.length === 0) return [];

      const statsPromises = farms.slice(0, 5).map(async (farm) => {
        try {
          const stats = await farmApi.getStats(farm.id);
          return {
            name: farm.name.length > 15 ? farm.name.slice(0, 15) + '...' : farm.name,
            ndvi: stats?.latestNdvi?.meanValue ?? null,
          };
        } catch {
          return {
            name: farm.name.length > 15 ? farm.name.slice(0, 15) + '...' : farm.name,
            ndvi: null,
          };
        }
      });

      return Promise.all(statsPromises);
    },
    enabled: farms.length > 0,
  });

  // Calculate crop distribution from actual farm data
  const cropDistribution = (() => {
    if (farms.length === 0) return [];

    const cropMap: Map<string, { count: number; area: number }> = new Map();

    for (const farm of farms) {
      const cropType = farm.cropType || 'Unknown';
      const existing = cropMap.get(cropType) || { count: 0, area: 0 };
      cropMap.set(cropType, {
        count: existing.count + 1,
        area: existing.area + (farm.areaHectares || 0),
      });
    }

    const total = farms.length;
    return Array.from(cropMap.entries()).map(([name, data]) => ({
      name,
      value: Math.round((data.count / total) * 100),
      area: Math.round(data.area * 10) / 10,
    }));
  })();

  // Calculate health metrics from real data
  const healthMetrics = (() => {
    const avgNdvi = ndviTrendData && ndviTrendData.length > 0
      ? ndviTrendData.reduce((sum, d) => sum + d.value, 0) / ndviTrendData.length
      : 0;

    const farmStatsAvgNdvi = farmStatsData && farmStatsData.length > 0
      ? farmStatsData.filter(f => f.ndvi !== null).reduce((sum, f) => sum + (f.ndvi || 0), 0) / farmStatsData.filter(f => f.ndvi !== null).length
      : 0;

    // Convert NDVI (0-1) to percentage metrics
    return [
      { metric: 'NDVI', value: Math.round(avgNdvi * 100), fullMark: 100 },
      { metric: 'Coverage', value: farms.length > 0 ? Math.round((farms.filter(f => f.boundary && f.boundary.length > 0).length / farms.length) * 100) : 0, fullMark: 100 },
      { metric: 'Current', value: Math.round(farmStatsAvgNdvi * 100), fullMark: 100 },
      { metric: 'Farms', value: Math.min(farms.length * 10, 100), fullMark: 100 },
    ];
  })();

  const farmComparison = farmStatsData?.filter(f => f.ndvi !== null) || [];

  const totalArea = farms.reduce((sum, f) => sum + (f.areaHectares || 0), 0);
  const avgNdvi = ndviTrendData && ndviTrendData.length > 0
    ? ndviTrendData.reduce((sum, d) => sum + d.value, 0) / ndviTrendData.length
    : null;

  // Calculate overall health score based on average NDVI
  const healthScore = avgNdvi !== null ? Math.round(avgNdvi * 100) : null;

  if (farmsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500">Cross-farm insights and trends</p>
        </div>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-4 py-2 border rounded-lg"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-sm text-gray-500">Total Farms</p>
          <p className="text-2xl font-bold">{farms.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-sm text-gray-500">Total Area</p>
          <p className="text-2xl font-bold">{totalArea.toFixed(1)} ha</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-sm text-gray-500">Avg NDVI</p>
          <p className="text-2xl font-bold text-green-600">
            {avgNdvi !== null ? avgNdvi.toFixed(3) : 'N/A'}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-sm text-gray-500">Health Score</p>
          <p className="text-2xl font-bold text-blue-600">
            {healthScore !== null ? `${healthScore}%` : 'N/A'}
          </p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* NDVI Trend */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">NDVI Trend (All Farms)</h3>
          <div className="h-72">
            {ndviTrendData && ndviTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={ndviTrendData}>
                  <defs>
                    <linearGradient id="ndviGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 1]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: number) => value.toFixed(3)} />
                  <Area type="monotone" dataKey="value" name="NDVI" stroke="#10b981" fill="url(#ndviGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                {farms.length === 0 ? 'Add farms to see NDVI trends' : 'No NDVI data available'}
              </div>
            )}
          </div>
        </div>

        {/* Crop Distribution */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Crop Distribution</h3>
          <div className="h-72 flex items-center">
            {cropDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={cropDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {cropDistribution.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(_value, name, props) => [`${props.payload.area} ha`, name]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500">
                Add farms to see crop distribution
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Health Radar */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Overall Health Metrics</h3>
          <div className="h-72">
            {healthMetrics.some(m => m.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={healthMetrics}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  <Radar name="Health" dataKey="value" stroke="#10b981" fill="#10b981" fillOpacity={0.5} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                Add farms and run analysis to see health metrics
              </div>
            )}
          </div>
        </div>

        {/* Farm Comparison */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Farm Comparison</h3>
          <div className="h-72">
            {farmComparison.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={farmComparison} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" domain={[0, 1]} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                  <Tooltip formatter={(value: number) => value.toFixed(3)} />
                  <Legend />
                  <Bar dataKey="ndvi" name="NDVI" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                {farms.length === 0 ? 'Add farms to see comparison' : 'No NDVI data available for comparison'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
